import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import {
  AccessLevel,
  ActivationStatus,
  Tags,
  TaskPriority,
  TaskStatus,
  UserStatus,
} from '../types';

const i18nMap: Record<Tags, string> = {
  [Tags.FAMILY]: 'family',
  [Tags.LEGAL]: 'legal',
  [Tags.WEALTH]: 'wealth',
  [Tags.TAX]: 'tax',
  [Tags.FINANCE]: 'finance',
  [Tags.MANAGEMENT]: 'management',
  [Tags.LIFESTYLE]: 'lifestyle',
};

export const useI18nValues = () => {
  const { t } = useTranslation('entities/vfo');

  return useMemo(
    () => ({
      tag: (tag: Tags) => t(`tags.${i18nMap[tag]}`),
      userStatus: (userStatus: UserStatus, activationStatus: ActivationStatus) => {
        switch (activationStatus) {
          case ActivationStatus.INVITED:
            return t('userStatus.invited');
          case ActivationStatus.PENDING:
            return t('userStatus.pending');
          case ActivationStatus.ACTIVE:
          default:
            return t(`userStatus.${userStatus.toLowerCase()}`);
        }
      },
      status: (taskStatus: TaskStatus) =>
        t(
          `taskStatus.${
            taskStatus === TaskStatus.IN_PROGRESS ? 'inProgress' : taskStatus.toLowerCase()
          }`,
        ),
      priority: (taskPriority: TaskPriority) => t(`priorities.${taskPriority.toLowerCase()}`),
      accessLevel: (accessLevel: AccessLevel) => {
        switch (accessLevel) {
          case AccessLevel.CONNECTED:
            return t('accessLevels.connected.title');
          case AccessLevel.DELEGATED:
            return t('accessLevels.delegated.title');
          default:
          case AccessLevel.BASIC:
            return t('accessLevels.basic.title');
        }
      },
      accessLevelDescription: (accessLevel: AccessLevel) => {
        const prefix = accessLevel.toLowerCase();

        const data = {
          title: t(`accessLevels.${prefix}.title`),
          description: t(`accessLevels.${prefix}.description`),
          allowsTitle: t(`accessLevels.${prefix}.allowsTitle`),
          allows1: t(`accessLevels.${prefix}.allows1`),
          allows2: t(`accessLevels.${prefix}.allows2`),
          allows3: t(`accessLevels.${prefix}.allows3`),
          notAllowsTitle: t(`accessLevels.${prefix}.notAllowsTitle`),
          notAllows1: t(`accessLevels.${prefix}.notAllows1`),
          notAllows2: t(`accessLevels.${prefix}.notAllows2`),
          notAllows3:
            accessLevel === AccessLevel.DELEGATED
              ? t(`accessLevels.${prefix}.notAllows3`)
              : undefined,
        };

        return data;
      },
    }),
    [t],
  );
};
